import React, { useEffect, useMemo, useRef } from 'react';
import type { DataType, PhotoProviderBase } from './types';
import useMethods from './hooks/useMethods';
import useSetState from './hooks/useSetState';
import PhotoContext from './photo-context';
import PhotoSlider from './PhotoSlider';
import { setSidebarWidth } from './utils/getInnerWidth';

export interface PhotoProviderProps extends PhotoProviderBase {
  children: React.ReactNode;
  onIndexChange?: (index: number, state: PhotoProviderState) => void;
  onVisibleChange?: (visible: boolean, index: number, state: PhotoProviderState) => void;
  protectionLayerEnable?: boolean;
  protectionLayerRender?: (props: { isVisible: boolean; onRestore: () => void; reason: 'focus' | 'command' | 'printscreen' }) => React.ReactNode;
}

type PhotoProviderState = {
  images: DataType[];
  visible: boolean;
  index: number;
  fullScreen: boolean;
};

const initialState: PhotoProviderState = {
  images: [],
  visible: false,
  index: 0,
  fullScreen: false,
};

export default function PhotoProvider({
  children,
  onIndexChange,
  onVisibleChange,
  sidebarLeftRender,
  sidebarRightRender,
  sidebarWidth = 400,
  protectionLayerEnable,
  protectionLayerRender,
  ...restProps
}: PhotoProviderProps) {
  const [state, updateState] = useSetState(initialState);
  const uniqueIdRef = useRef(0);
  const { images, visible, index } = state;

  useEffect(() => {
    setSidebarWidth(sidebarLeftRender || sidebarRightRender ? sidebarWidth : 0);
  }, [sidebarLeftRender, sidebarRightRender, sidebarWidth]);

  const methods = useMethods({
    nextId() {
      return (uniqueIdRef.current += 1);
    },
    update(imageItem: DataType) {
      const currentIndex = images.findIndex((n) => n.key === imageItem.key);
      if (currentIndex > -1) {
        const nextImages = images.slice();
        nextImages.splice(currentIndex, 1, imageItem);
        updateState({
          images: nextImages,
        });
        return;
      }
      updateState((prev) => ({
        images: prev.images.concat(imageItem),
      }));
    },
    remove(key: number) {
      updateState((prev) => {
        const nextImages = prev.images.filter((item) => item.key !== key);
        const nextEndIndex = nextImages.length - 1;
        return {
          images: nextImages,
          index: Math.min(nextEndIndex, index),
        };
      });
    },
    show(key: number) {
      const currentIndex = images.findIndex((item) => item.key === key);
      updateState({
        visible: true,
        index: currentIndex,
      });
      if (onVisibleChange) {
        onVisibleChange(true, currentIndex, state);
      }
    },
  });

  const fn = useMethods({
    close() {
      updateState({
        visible: false,
      });

      if (onVisibleChange) {
        onVisibleChange(false, index, state);
      }
    },
    changeIndex(nextIndex: number) {
      updateState({
        index: nextIndex,
      });

      if (onIndexChange) {
        onIndexChange(nextIndex, state);
      }
    },
    fullScreen(status: boolean) {
      updateState({
        fullScreen: status,
      });
    },
  });

  const value = useMemo(() => ({ ...state, ...methods }), [state, methods]);

  return (
    <PhotoContext.Provider value={value}>
      {children}
      <PhotoSlider
        images={images}
        visible={visible}
        index={index}
        fullScreen={state.fullScreen}
        onIndexChange={fn.changeIndex}
        onClose={fn.close}
        onFullScreen={fn.fullScreen}
        sidebarWidth={sidebarWidth}
        sidebarLeftRender={sidebarLeftRender}
        sidebarRightRender={sidebarRightRender}
        protectionLayerEnable={protectionLayerEnable}
        protectionLayerRender={protectionLayerRender}
        {...restProps}
      />
    </PhotoContext.Provider>
  );
}
